import websiteConfig from '@/settings';
import { JsonResponse } from '@/types/JsonResponse';
import {
  ResponseHandler,
  ResponseInfo,
  ResponseError,
  ResponseCallback,
} from '@/types/ResponseHandler';
import router from '@/router';
import store from '../store';

const requestBase = {
  mode: 'cors',
  credentials: 'include',
  'Content-Security-Policy': 'prefetch-src https://cdn.amcharts.com/lib/',
} as RequestInit;

async function getRequestBase(url: string): Promise<Response> {
  // const request = cloneDeep(requestBase);
  const request = JSON.parse(JSON.stringify(requestBase));
  request.method = 'get';
  request.headers = {
    'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
  };
  return fetch(websiteConfig.APIWebsiteUrl + url, request);
}

async function postRequestBase<T>(url: string, body: T): Promise<Response> {
  const request = JSON.parse(JSON.stringify(requestBase));
  request.method = 'post';
  request.headers = {
    'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
  };
  request.body = JSON.stringify(body);
  return fetch(websiteConfig.APIWebsiteUrl + url, request);
}

async function fileRequestBase(url: string, body: FormData): Promise<Response> {
  const request = JSON.parse(JSON.stringify(requestBase));
  request.method = 'post';
  request.body = body;
  return fetch(websiteConfig.APIWebsiteUrl + url, request);
}

async function handleHttpError(httpStatus: number): Promise<null | string> {
  switch (httpStatus) {
    case 400:
      return 'Bad request.';
    case 404:
      window.location.href = '/article/404/';
      return null;
    case 405:
      return 'Method not allowed.';
    case 500:
      return 'Server error. We\'ve been alarmed and will fix it soon.';
    default:
      return 'Unexpected error.';
  }
}

async function handleResponse<T>(
  response: Response,
  responseHandlers?: (ResponseCallback | ResponseInfo | ResponseError)[],
): Promise<null | T> {
  if (response.ok) {
    const jsonData = await response.json() as JsonResponse<T>;
    if (jsonData === undefined) {
      store.commit('pushError', 'Server returned empty response');
      return null;
    }
    if (jsonData.data === undefined) {
      store.commit('pushError', 'Server returned empty response');
      return null;
    }
    // else store.commit('setEmailConfirmed', true)
    return jsonData.data as T;
  }
  return null;
}

export async function getRequest<T>(
  url: string,
  responseHandlers?: Array<ResponseHandler>,
): Promise<T | null> {
  let response: Response | null = null;
  try {
    response = await getRequestBase(url);
  } catch (error) {
    return null;
  }
  return handleResponse(await response, responseHandlers);
}

export async function postRequest<TRequest, TResponse>(
  url: string,
  data: TRequest,
  responseHandlers?: Array<ResponseHandler>,
): Promise<TResponse | null> {
  let response: Response | null = null;
  try {
    response = await postRequestBase<TRequest>(url, data);
  } catch (error) {
    return null;
  }
  return handleResponse(response, responseHandlers);
}

export async function deleteRequest(
  url: string,
  responseHandlers?: Array<ResponseHandler>,
): Promise<Record<string, never> | null> {
  let response: Response | null = null;
  try {
    response = await postRequestBase<Record<string, never>>(url, {});
  } catch (error) {
    store.commit('pushError', 'Connection timeout');
    return null;
  }
  return handleResponse(response, responseHandlers);
}

export async function fileRequest<T>(
  url: string,
  data: FormData,
  responseHandlers?: Array<ResponseHandler>,
): Promise<T | null> {
  let response: Response | null = null;
  try {
    response = await fileRequestBase(url, data);
  } catch (error) {
    store.commit('pushError', 'Connection timeout');
    return null;
  }
  return handleResponse(response, responseHandlers);
}
